import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalIcon,
  Clock,
  Video,
  Check,
  Loader2,
  AlertCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Direct axios — public, unauthenticated page.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────────────────────── helpers ─────────────────────────

const monthLabel = (date) =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const buildMonthGrid = (year, monthIdx) => {
  const first = new Date(year, monthIdx, 1);
  const start = new Date(first);
  // Start on Sunday
  start.setDate(first.getDate() - first.getDay());
  const cells = [];
  const c = new Date(start);
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(c));
    c.setDate(c.getDate() + 1);
  }
  return cells;
};

// ───────────────────────── timezone helpers ─────────────────────────
//
// Calendly-style rule:
//   • The HOST timezone (data.timezone) only matters internally — the backend
//     uses it to compute working-hour availability against the host's calendar.
//   • The VIEWER (person booking) should ALWAYS see slots, dates and the
//     confirmation header in their OWN local time.
//   • We default to the browser's IANA TZ, but expose a selector for visitors
//     who travel ("Show times in: America/New_York ▾").
//   • The confirmation payload sent to the backend is still UTC ISO strings
//     (s.start / s.end), so the host's calendar event lands at the correct
//     wall-clock time regardless of what the visitor saw.

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const fmtTime = (iso, tz) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz || undefined,
  });

const fmtLongDay = (iso, tz) =>
  new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: tz || undefined,
  });

const fmtConfirmHeader = (iso, tz) =>
  new Date(iso).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz || undefined,
  });

const fmtTzAbbrev = (tz) => {
  // Renders e.g. "EDT", "CST", "GMT+2"
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
};

// ─────────────────────────  page  ─────────────────────────

export default function PublicBooking() {
  const { token, slug } = useParams();
  const isPreview = token === "preview";
  const isPersonal = !!slug;
  // Lead one-shot mode (default) = !isPersonal

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Viewer timezone — the LOCAL timezone of whoever is looking at the page,
  // NOT the host's timezone. Defaults to the browser's IANA TZ. The selector
  // lets a traveler override it without affecting how the slot is stored on
  // the host's calendar (ISO timestamps are timezone-agnostic).
  const [viewerTz, setViewerTz] = useState(() => browserTz());

  // Calendar/UI state
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [pickedDay, setPickedDay] = useState(null);
  const [picked, setPicked] = useState(null);
  const [step, setStep] = useState("pick"); // pick | form | confirming | done

  // Form fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  // Load data
  useEffect(() => {
    (async () => {
      try {
        let res;
        if (isPreview) {
          const auth = localStorage.getItem("lf_token") || "";
          const wid = localStorage.getItem("lf_workspace_id") || "";
          res = await axios.get(`${API}/booking/preview`, {
            headers: {
              ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
              ...(wid ? { "X-Workspace-Id": wid } : {}),
            },
          });
        } else if (isPersonal) {
          res = await axios.get(`${API}/booking/personal/${slug}`);
        } else {
          res = await axios.get(`${API}/booking/public/${token}`);
        }
        setData(res.data);
        const firstSlot = (res.data.slots || [])[0];
        if (firstSlot) {
          const d = new Date(firstSlot.start);
          setViewYear(d.getFullYear());
          setViewMonth(d.getMonth());
          setPickedDay(d);
        }
        const lead = res.data?.lead;
        if (lead) {
          setEmail(lead.email || "");
          setFirstName(lead.first_name || "");
          setCompany(lead.business_name || "");
          setPhone(lead.phone || "");
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        setError(
          isPreview && e.response?.status === 401
            ? "Sign in to preview the booking page."
            : detail || "This booking link is invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token, slug, isPreview, isPersonal]);

  // Index of slots by yyyy-mm-dd → list. KEY IS BUILT IN THE VIEWER'S
  // TIMEZONE so the calendar grid groups slots by the viewer's local day,
  // not the host's day. (Without this, an 11pm EDT slot would land on
  // "tomorrow" in the host's tz if the host is in CET.)
  const slotsByDay = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const s of data.slots || []) {
      const d = new Date(s.start);
      const key = d.toLocaleDateString("en-CA", { timeZone: viewerTz }); // YYYY-MM-DD
      (out[key] = out[key] || []).push(s);
    }
    return out;
  }, [data, viewerTz]);

  const daysWithSlots = useMemo(() => new Set(Object.keys(slotsByDay)), [slotsByDay]);

  const monthGrid = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const pickedKey = pickedDay
    ? pickedDay.toLocaleDateString("en-CA", { timeZone: viewerTz })
    : null;
  const slotsForPicked = pickedKey ? slotsByDay[pickedKey] || [] : [];

  // Confirm flow
  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isPreview) {
        await new Promise((r) => setTimeout(r, 600));
        setConfirmed({ preview: true, start: picked.start, end: picked.end, timezone: viewerTz });
        setStep("done");
        return;
      }
      let resp;
      if (isPersonal) {
        resp = await axios.post(`${API}/booking/personal/${slug}/confirm`, {
          start: picked.start,
          end: picked.end,
          first_name: firstName,
          last_name: lastName || null,
          email,
          phone: phone || null,
          company: company || null,
          note: note || null,
          answers,
        });
      } else {
        resp = await axios.post(`${API}/booking/public/${token}/confirm`, {
          start: picked.start,
          end: picked.end,
          email: email || null,
          note: note || null,
          answers,
        });
      }
      setConfirmed(resp.data);
      setStep("done");
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Could not confirm. Please try another time.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── loading / error ───
  if (loading)
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: data?.branding?.brand_color || "#002FA7" }} />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4" data-testid="booking-error">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl p-10 text-center shadow-sm">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-bold text-xl mb-2">Link unavailable</h1>
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
        <p className="text-xs text-zinc-400 mt-6 text-center">
          Powered by Scalater ·{" "}
          <a
            href="https://www.scalater.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-zinc-600 underline-offset-2 hover:underline"
            data-testid="booking-error-privacy-link"
          >
            Privacy
          </a>
        </p>
      </div>
    );

  const brand = data.branding || {};
  const brandColor = brand.brand_color || "#002FA7";
  const questions = data.questions || [];

  // ─── confirmed view ───
  if (step === "done" && confirmed)
    return (
      <ConfirmedView
        confirmed={confirmed}
        brand={brand}
        brandColor={brandColor}
        data={data}
        isPreview={isPreview}
        viewerTz={viewerTz}
      />
    );

  // ─── main 2-col layout ───
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="booking-page">
      {isPreview && <PreviewBanner calendarConnected={data?.calendar_connected} />}

      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-14">
        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="grid lg:grid-cols-[380px_1fr]">
            {/* ── Left column: branding ── */}
            <aside className="p-8 lg:p-10 lg:border-r border-zinc-200 lg:min-h-[640px]">
              <BrandHeader brand={brand} brandColor={brandColor} data={data} />
            </aside>

            {/* ── Right column: calendar + slots / form ── */}
            <section className="p-6 lg:p-10">
              {step === "pick" && (
                <PickStep
                  data={data}
                  monthGrid={monthGrid}
                  daysWithSlots={daysWithSlots}
                  viewYear={viewYear}
                  viewMonth={viewMonth}
                  setViewYear={setViewYear}
                  setViewMonth={setViewMonth}
                  pickedDay={pickedDay}
                  setPickedDay={(d) => {
                    setPickedDay(d);
                    setPicked(null);
                  }}
                  slotsForPicked={slotsForPicked}
                  picked={picked}
                  setPicked={setPicked}
                  brandColor={brandColor}
                  onNext={() => setStep("form")}
                  viewerTz={viewerTz}
                  setViewerTz={setViewerTz}
                />
              )}

              {step === "form" && (
                <FormStep
                  isPersonal={isPersonal}
                  picked={picked}
                  data={data}
                  brandColor={brandColor}
                  questions={questions}
                  email={email}
                  setEmail={setEmail}
                  firstName={firstName}
                  setFirstName={setFirstName}
                  lastName={lastName}
                  setLastName={setLastName}
                  phone={phone}
                  setPhone={setPhone}
                  company={company}
                  setCompany={setCompany}
                  note={note}
                  setNote={setNote}
                  answers={answers}
                  setAnswers={setAnswers}
                  onBack={() => setStep("pick")}
                  onSubmit={submit}
                  submitting={submitting}
                  submitError={submitError}
                  viewerTz={viewerTz}
                />
              )}
            </section>
          </div>
        </div>

        <p className="text-xs text-zinc-400 mt-4 text-center">
          Powered by Scalater ·{" "}
          <a
            href="https://www.scalater.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-zinc-600 underline-offset-2 hover:underline"
            data-testid="booking-privacy-link"
          >
            Privacy
          </a>
        </p>
      </div>
    </div>
  );
}

// ───────────────────────── sub-components ─────────────────────────

function BrandHeader({ brand, brandColor, data }) {
  return (
    <div className="space-y-5">
      {brand.logo_url ? (
        <img
          src={brand.logo_url}
          alt="logo"
          className="w-14 h-14 rounded-full object-cover border border-zinc-200"
          data-testid="brand-logo"
          onError={(e) => {
            // Graceful degrade: hide the broken image and render the
            // initial-letter fallback right next to it.
            const fb = e.currentTarget.nextElementSibling;
            e.currentTarget.style.display = "none";
            if (fb) fb.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="w-14 h-14 rounded-full items-center justify-center text-white font-bold text-lg"
        style={{
          background: brandColor,
          display: brand.logo_url ? "none" : "flex",
        }}
        data-testid="brand-logo-fallback"
      >
        {(data.workspace_name || "S").slice(0, 1).toUpperCase()}
      </div>

      <div>
        <div className="text-sm font-semibold text-zinc-500" data-testid="brand-subheadline">
          {brand.subheadline || data.workspace_name}
        </div>
        <h1 className="font-bold text-3xl text-zinc-900 mt-1 leading-tight" data-testid="brand-headline">
          {brand.headline || `${data.duration_min} Minute Meeting`}
        </h1>
      </div>

      <ul className="space-y-2.5 text-sm text-zinc-600">
        <li className="flex items-center gap-2.5">
          <Clock className="w-4 h-4 text-zinc-400" />
          <span>{data.duration_min} min</span>
        </li>
        <li className="flex items-start gap-2.5">
          <Video className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
          <span>{brand.web_conf_note}</span>
        </li>
        <li className="flex items-center gap-2.5">
          <CalIcon className="w-4 h-4 text-zinc-400" />
          <span className="text-xs text-zinc-500">Host in {data.timezone}</span>
        </li>
      </ul>

      {brand.description && (
        <div className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap pt-3 border-t border-zinc-100" data-testid="brand-description">
          {brand.description}
        </div>
      )}

      {data?.lead?.business_name && (
        <div className="text-xs text-zinc-500 pt-3 border-t border-zinc-100">
          Booking for{" "}
          <span className="font-semibold text-zinc-700">{data.lead.business_name}</span>
          {data.lead.first_name ? ` — ${data.lead.first_name}` : ""}
        </div>
      )}
    </div>
  );
}

function PickStep({
  data,
  monthGrid,
  daysWithSlots,
  viewYear,
  viewMonth,
  setViewYear,
  setViewMonth,
  pickedDay,
  setPickedDay,
  slotsForPicked,
  picked,
  setPicked,
  brandColor,
  onNext,
  viewerTz,
  setViewerTz,
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthDate = new Date(viewYear, viewMonth, 1);

  const stepMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  return (
    <div className="grid md:grid-cols-[1fr_220px] gap-8 min-h-[520px]">
      {/* Calendar */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-lg text-zinc-900">Select a Date &amp; Time</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => stepMonth(-1)}
              data-testid="cal-prev"
              className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-semibold text-zinc-800 px-2 min-w-[140px] text-center" data-testid="cal-month-label">
              {monthLabel(monthDate)}
            </div>
            <button
              type="button"
              onClick={() => stepMonth(1)}
              data-testid="cal-next"
              className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-2 px-1">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i} className="text-center">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {monthGrid.map((d, i) => {
            const inMonth = d.getMonth() === viewMonth;
            const past = d < today;
            const key = d.toLocaleDateString("en-CA", { timeZone: viewerTz });
            const available = daysWithSlots.has(key);
            const selected = pickedDay && sameDay(d, pickedDay);
            const clickable = inMonth && !past && available;
            return (
              <button
                key={i}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && setPickedDay(d)}
                data-testid={clickable ? `cal-day-${key}` : undefined}
                className={[
                  "aspect-square rounded-full text-sm flex items-center justify-center transition-colors",
                  !inMonth ? "text-zinc-300" : past ? "text-zinc-300" : "",
                  clickable && !selected ? "text-white font-semibold hover:opacity-90" : "",
                  !clickable && inMonth && !past ? "text-zinc-400" : "",
                  selected ? "font-bold ring-2 ring-offset-2" : "",
                ].join(" ")}
                style={
                  clickable && !selected
                    ? { background: brandColor }
                    : selected
                      ? { background: "#fff", color: brandColor, borderColor: brandColor }
                      : undefined
                }
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slots column */}
      <div className="md:border-l md:pl-8 md:border-zinc-100">
        <div className="text-sm font-semibold text-zinc-800 mb-1" data-testid="picked-day-label">
          {pickedDay ? pickedDay.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "Pick a day"}
        </div>
        <TimezoneSelector value={viewerTz} onChange={setViewerTz} hostTz={data.timezone} />

        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="slot-list">
          {slotsForPicked.length === 0 && (
            <div className="text-xs text-zinc-500" data-testid="no-slots-day">
              No availability on this day.
            </div>
          )}
          {slotsForPicked.map((s) => {
            const active = picked?.start === s.start;
            return (
              <div key={s.start} className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPicked(s)}
                  data-testid={`slot-${s.start}`}
                  className="flex-1 px-3 py-2.5 text-sm border rounded-md font-mono transition-colors"
                  style={
                    active
                      ? { background: "#fff", borderColor: brandColor, color: brandColor }
                      : { borderColor: "#e5e7eb", color: "#374151" }
                  }
                >
                  {fmtTime(s.start, viewerTz)}
                </button>
                {active && (
                  <button
                    type="button"
                    onClick={onNext}
                    data-testid="btn-confirm-slot-next"
                    className="px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white rounded-md transition-colors"
                    style={{ background: brandColor }}
                  >
                    Next
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FormStep({
  isPersonal,
  picked,
  data,
  brandColor,
  questions,
  email,
  setEmail,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  phone,
  setPhone,
  company,
  setCompany,
  note,
  setNote,
  answers,
  setAnswers,
  onBack,
  onSubmit,
  submitting,
  submitError,
  viewerTz,
}) {
  return (
    <div className="max-w-xl">
      <button
        type="button"
        onClick={onBack}
        data-testid="btn-form-back"
        className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="w-3 h-3" /> Back to times
      </button>

      <h2 className="font-bold text-lg text-zinc-900 mb-1">Enter Details</h2>
      <div className="text-sm text-zinc-500 mb-6" data-testid="form-picked-summary">
        {picked && fmtConfirmHeader(picked.start, viewerTz)} ({fmtTzAbbrev(viewerTz)})
      </div>

      <div className="space-y-4">
        {isPersonal && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="input-first-name"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
              />
            </Field>
            <Field label="Last name">
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="input-last-name"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
              />
            </Field>
          </div>
        )}

        <Field label="Email *">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="input-booking-email"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
          />
        </Field>

        {isPersonal && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="input-booking-phone"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
              />
            </Field>
            <Field label="Company (optional)">
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                data-testid="input-booking-company"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
              />
            </Field>
          </div>
        )}

        {questions.map((q) => (
          <Field
            key={q.id}
            label={`${q.label}${q.required ? " *" : ""}`}
            testId={`question-${q.id}`}
          >
            <QuestionInput
              q={q}
              value={answers[q.id]}
              onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
            />
          </Field>
        ))}

        {!isPersonal && (
          <Field label="Anything we should know? (optional)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              data-testid="input-booking-note"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
            />
          </Field>
        )}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !email || (isPersonal && !firstName)}
          data-testid="btn-confirm-booking"
          className="w-full text-white py-3 text-sm font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
          style={{ background: brandColor }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Schedule Event
        </button>

        {submitError && (
          <div
            className="mt-3 px-3 py-2 border border-red-300 bg-red-50 text-xs text-red-700 flex items-start gap-2 rounded-md"
            data-testid="booking-confirm-error"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="block text-xs text-zinc-500 font-semibold mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.kind === "textarea") {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={q.placeholder || ""}
        data-testid={`q-input-${q.id}`}
        className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
      />
    );
  }
  if (q.kind === "select" && (q.options || []).length > 0) {
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`q-input-${q.id}`}
        className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800 bg-white"
      >
        <option value="">— Select —</option>
        {q.options.map((opt, i) => (
          <option key={i} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  if (q.kind === "yes_no") {
    return (
      <div className="flex gap-2" data-testid={`q-input-${q.id}`}>
        {["Yes", "No"].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-4 py-2 text-sm border rounded-md font-semibold transition-colors ${
              value === opt
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={q.placeholder || ""}
      data-testid={`q-input-${q.id}`}
      className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:border-zinc-800"
    />
  );
}

function ConfirmedView({ confirmed, brand, brandColor, data, isPreview, viewerTz }) {
  // After confirm: use the visitor's viewer TZ (or fallback) so they see the
  // meeting time in their own time. The host receives it in their own TZ via
  // Google Calendar (already correctly localized on their end).
  const displayTz = viewerTz || confirmed.timezone || data?.timezone;
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="booking-confirmed">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl p-10 text-center shadow-sm">
        {isPreview && <PreviewBanner small calendarConnected={data?.calendar_connected} />}
        <div
          className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: brandColor + "22" }}
        >
          <Check className="w-7 h-7" style={{ color: brandColor }} />
        </div>
        <h1 className="font-bold text-2xl mb-2">
          {isPreview ? "Preview: prospect sees this screen" : "You're booked!"}
        </h1>
        <p className="text-sm text-zinc-600 mb-1">
          {fmtLongDay(confirmed.start, displayTz)}
        </p>
        <p className="text-sm text-zinc-700 font-semibold mb-3">
          {fmtTime(confirmed.start, displayTz)}
        </p>
        <p className="text-xs text-zinc-400 mb-6">Timezone: {displayTz} ({fmtTzAbbrev(displayTz)})</p>
        {confirmed.html_link && (
          <a
            href={confirmed.html_link}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-xs underline hover:no-underline"
            style={{ color: brandColor }}
          >
            View on Google Calendar →
          </a>
        )}
        <p className="text-xs text-zinc-400 mt-6">
          {isPreview
            ? "(Nothing was actually booked — this is a preview.)"
            : `${brand.web_conf_note || "A calendar invite has been sent."} See you soon.`}
        </p>
      </div>
    </div>
  );
}

function PreviewBanner({ small = false, calendarConnected = true }) {
  return (
    <div
      data-testid="preview-banner"
      className={`bg-amber-50 border border-amber-200 text-amber-900 ${
        small ? "px-3 py-2 mb-4 text-[10px] rounded-md" : "px-4 py-3 text-xs"
      } flex items-start gap-2 max-w-6xl mx-auto`}
    >
      <Eye className={small ? "w-3 h-3 mt-0.5 shrink-0" : "w-4 h-4 mt-0.5 shrink-0"} />
      <div>
        <span className="font-bold uppercase tracking-wider">Preview mode</span>
        {" — "}This is exactly what your prospect will see when the AI sends them the booking link.
        {!calendarConnected && (
          <span className="block mt-1 text-amber-700">
            Google Calendar not connected → showing mocked slots. Connect Calendar in Settings to see real availability.
          </span>
        )}
      </div>
    </div>
  );
}

// Common timezones offered in the dropdown. We seed with the viewer's own
// browser TZ + the host TZ at the top so the visitor immediately sees the
// most likely correct option without scrolling.
const COMMON_TZ = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "America/Mexico_City", "America/Bogota", "America/Lima", "America/Santiago",
  "America/Buenos_Aires", "America/Sao_Paulo",
  "Europe/London", "Europe/Madrid", "Europe/Paris", "Europe/Berlin",
  "Europe/Amsterdam", "Europe/Rome", "Europe/Athens", "Europe/Istanbul",
  "Africa/Cairo", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Shanghai",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Pacific/Auckland",
  "UTC",
];

function TimezoneSelector({ value, onChange, hostTz }) {
  // De-dupe: visitor's TZ first, host's TZ next, then the curated common list.
  const options = useMemo(() => {
    const seen = new Set();
    const list = [];
    [value, hostTz, ...COMMON_TZ].forEach((tz) => {
      if (tz && !seen.has(tz)) {
        seen.add(tz);
        list.push(tz);
      }
    });
    return list;
  }, [value, hostTz]);
  return (
    <div className="mb-4">
      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1">
        Times shown in
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid="viewer-tz-selector"
          className="w-full text-xs px-2 py-1.5 border border-zinc-200 rounded focus:outline-none focus:border-zinc-400 bg-white appearance-none pr-7 cursor-pointer"
        >
          {options.map((tz) => (
            <option key={tz} value={tz}>
              {tz} ({fmtTzAbbrev(tz)})
            </option>
          ))}
        </select>
        <ChevronRight className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none text-zinc-400" />
      </div>
      {hostTz && hostTz !== value && (
        <div className="text-[10px] text-zinc-400 mt-1">Host calendar in {hostTz}</div>
      )}
    </div>
  );
}

